import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { qrApi, productsApi } from '../lib/api';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { MagnifyingGlass, CircleNotch, Package, QrCode, ArrowRight, CheckCircle, Truck } from '@phosphor-icons/react';
import { getStatusColor, formatDate } from '../lib/utils';
import { toast } from 'sonner';

export default function QRCodesPage() {
  const { t } = useTranslation();
  const [masters, setMasters] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterProduct, setFilterProduct] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [masterRes, prodRes] = await Promise.all([
        qrApi.masterSummary(),
        productsApi.list(),
      ]);
      setMasters(masterRes.data);
      setProducts(prodRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const getProduct = useCallback((productId) => products.find(p => p.id === productId), [products]);

  const filtered = useMemo(() => {
    return masters.filter(qr => {
      if (filterProduct !== 'all' && qr.product_id !== filterProduct) return false;
      if (filterStatus !== 'all' && qr.status !== filterStatus) return false;
      if (search) {
        const q = search.trim().toLowerCase();
        const product = getProduct(qr.product_id);
        // Exact match on QR ID or batch number; product name still allows contains for usability
        const qrExact = (qr.qr_id || '').toLowerCase() === q;
        const batchExact = (product?.batch_number || '').toLowerCase() === q;
        const nameHit = (product?.name || '').toLowerCase().includes(q) && q.length >= 2;
        if (!qrExact && !batchExact && !nameHit) return false;
      }
      return true;
    });
  }, [masters, filterProduct, filterStatus, search, getProduct]);

  const getProgressPercent = (cs) => {
    if (!cs || !cs.total || cs.total === 0) return 0;
    return Math.round((cs.delivered / cs.total) * 100);
  };

  const getProgressColor = (pct) => {
    if (pct >= 100) return 'bg-emerald-500';
    if (pct >= 50) return 'bg-brand-500';
    if (pct > 0) return 'bg-amber-500';
    return 'bg-zinc-400';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="qr-codes-page">
        <PageHeader
          title={t('qr.title')}
          description={`${filtered.length} ${t('qr.batches')} · ${t('qr.selectBatch')}`}
        />

        {/* Filters */}
        <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <MagnifyingGlass className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`} />
                <Input placeholder={t('qr.searchBatch')} value={search} onChange={e => setSearch(e.target.value)}
                  className={`pl-9 ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''}`} data-testid="search-qr-input" />
              </div>
              <Select value={filterProduct} onValueChange={setFilterProduct}>
                <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="filter-product">
                  <SelectValue placeholder="All Products" />
                </SelectTrigger>
                <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                  <SelectItem value="all">{t('qr.allProducts')}</SelectItem>
                  {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className={isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-100' : ''} data-testid="filter-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent className={isDark ? 'bg-zinc-800 border-zinc-700' : ''}>
                  <SelectItem value="all">{t('qr.allStatuses')}</SelectItem>
                  <SelectItem value="created">{t('qr.created')}</SelectItem>
                  <SelectItem value="in_transit">{t('qr.inTransit')}</SelectItem>
                  <SelectItem value="shipped">{t('qr.shipped')}</SelectItem>
                  <SelectItem value="delivered">{t('qr.delivered')}</SelectItem>
                </SelectContent>
              </Select>
              <div className={`text-sm flex items-center ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                {filtered.length} of {masters.length}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Master QR Tiles */}
        {loading ? (
          <div className="flex justify-center py-16">
            <CircleNotch className="w-8 h-8 animate-spin text-brand-500" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
            <CardContent className="py-16 text-center">
              <Package className={`w-12 h-12 mx-auto mb-3 opacity-30 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`} />
              <p className={isDark ? 'text-zinc-500' : 'text-zinc-400'}>{t('qr.noBatches')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map(qr => {
              const product = getProduct(qr.product_id);
              const cs = qr.child_status || {};
              const pct = getProgressPercent(cs);
              return (
                <div
                  key={qr.qr_id}
                  onClick={() => navigate(`/qr-codes/${encodeURIComponent(qr.qr_id)}`)}
                  className={`group relative rounded-xl border overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 ${
                    isDark
                      ? 'bg-zinc-900 border-zinc-800 hover:border-brand-500/40'
                      : 'bg-white border-zinc-200 hover:border-brand-300'
                  }`}
                  data-testid={`master-tile-${qr.qr_id}`}
                >
                  {/* QR Image */}
                  <div className={`relative flex justify-center p-4 ${isDark ? 'bg-zinc-950/50' : 'bg-slate-50'}`}>
                    {qr.qr_image ? (
                      <img src={`data:image/png;base64,${qr.qr_image}`} alt="Master QR" className="w-28 h-28 object-contain" />
                    ) : (
                      <QrCode className={`w-20 h-20 ${isDark ? 'text-zinc-700' : 'text-zinc-300'}`} />
                    )}
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-bold bg-brand-600 text-white tracking-wide">
                      MASTER
                    </span>
                  </div>

                  {/* Info */}
                  <div className="p-4 space-y-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className={`text-sm font-semibold truncate ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                          {product?.name || 'Unknown Product'}
                        </p>
                        {product?.batch_number && (
                          <p className={`text-xs font-mono mt-0.5 ${isDark ? 'text-brand-400' : 'text-brand-600'}`}>
                            {product.batch_number}
                          </p>
                        )}
                      </div>
                      <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-medium border ${getStatusColor(qr.status)}`}>
                        {qr.status}
                      </span>
                    </div>

                    <div className={`flex items-center justify-between text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                      <span className="flex items-center gap-1">
                        <QrCode className="w-3.5 h-3.5" />
                        {cs.total || qr.child_count || 0} {t('qr.qrCodes')}
                      </span>
                      <span>{formatDate(qr.created_at)}</span>
                    </div>

                    {/* Progress Bar */}
                    <div className="space-y-1" data-testid={`progress-${qr.qr_id}`}>
                      <div className={`w-full h-1.5 rounded-full ${isDark ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${getProgressColor(pct)}`}
                          style={{ width: `${Math.max(pct, 2)}%` }}
                        />
                      </div>
                      <div className={`flex items-center justify-between text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                        <span className="flex items-center gap-0.5">
                          {pct >= 100 ? <CheckCircle weight="fill" className="w-3 h-3 text-emerald-500" /> : <Truck className="w-3 h-3" />}
                          {cs.delivered || 0}/{cs.total || 0} {t('qr.delivered')}
                        </span>
                        <span>{pct}%</span>
                      </div>
                    </div>

                    {/* Navigate hint */}
                    <div className={`flex items-center justify-end gap-1 text-xs font-medium transition-colors ${
                      isDark ? 'text-zinc-600 group-hover:text-brand-400' : 'text-zinc-300 group-hover:text-brand-600'
                    }`}>
                      {t('qr.viewCodes')} <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
